import { getDevices } from '@websrc/App/Helpers/getDevices'
import { getLanguageIndexForLocaleWithLanguages } from '@websrc/App/Helpers/getLanguageIndexForLocaleWithLanguages'
import { useLocale } from '@websrc/App/Hooks/useLocale'
import { mapCurriculum } from '@websrc/DataMappers/mapSpot'
import {
  CSSoftwareApplications,
  CSSoftwareApplications_Language_Entry,
} from '@websrc/Typings/ContentStack'
import { useMemo, useState } from 'react'

export function usePlatformsLanguagesCurriculum(
  spot: ReturnType<typeof mapCurriculum>['curriculum']
) {
  const locale = useLocale()
  const [device] = getDevices()

  const filteredPlatforms = useMemo(() => {
    const filteredOS: CSSoftwareApplications['operating_system'][] = []

    spot.curriculum.forEach((curri) => {
      curri.downloads.forEach((download) => {
        if (!filteredOS.includes(download.operating_system)) {
          filteredOS.push(download.operating_system)
        }
      })
    })

    // If the are more OS'es don't include all devices in the list
    if (filteredOS.length > 1) {
      return filteredOS.filter((os) => os !== 'All devices')
    }

    return filteredOS
  }, [spot])

  const initialDeviceI = filteredPlatforms.findIndex((p) => p === device)

  const [selectedPlatformI, setSelectedPlatformI] = useState(Math.max(0, initialDeviceI))

  const filteredLanguages = useMemo(() => {
    const selectedOS = filteredPlatforms[selectedPlatformI]

    const filteredLangs: CSSoftwareApplications_Language_Entry['entry']['language'][] = []

    spot.curriculum.forEach((curri) => {
      curri.downloads.forEach((download) => {
        if (
          download.operating_system === selectedOS ||
          download.operating_system === 'All devices'
        ) {
          download.language.forEach(({ language }) => {
            if (!filteredLangs.includes(language)) {
              filteredLangs.push(language)
            }
          })
        }
      })
    })

    return filteredLangs
  }, [spot, filteredPlatforms, selectedPlatformI])

  const [selectedLangI, setSelectedLangI] = useState(
    getLanguageIndexForLocaleWithLanguages(locale, filteredLanguages)
  )

  const filteredCurriculum = useMemo(
    () =>
      spot.curriculum.filter((curri) =>
        Boolean(
          curri.downloads
            .filter((download) =>
              Boolean(
                download.language.find(
                  ({ language }) => language === filteredLanguages[selectedLangI]
                )
              )
            )
            .find(
              (download) =>
                download.operating_system === filteredPlatforms[selectedPlatformI] ||
                download.operating_system === 'All devices'
            )
        )
      ),
    [spot, filteredLanguages, filteredPlatforms, selectedPlatformI, selectedLangI]
  )

  return [
    filteredPlatforms,
    filteredLanguages,
    filteredCurriculum,
    selectedPlatformI,
    setSelectedPlatformI,
    selectedLangI,
    setSelectedLangI,
  ] as [
    typeof filteredPlatforms,
    typeof filteredLanguages,
    typeof filteredCurriculum,
    number,
    (platformI: number) => void,
    number,
    (langI: number) => void
  ]
}
